/**
 * `02/ Inquiries/ Table` (12:774) — `HeaderRow` (12:1184), the rows (12:1504 …) and
 * `Table/Pagination` (12:3252).
 *
 * Measured column widths, in order: 48 (checkbox) · flexible (CUSTOMER) · 190 (STAGE) ·
 * 230 (MODULES) · 140 (QUOTED) · 170 (RECEIVED) · 140 (DAYS IN STAGE) · 190 (OWNER) ·
 * 48 (actions). Row h-48 with a `border/default` bottom rule; header h-40 on `bg/subtle`
 * with `TableHeader/11` labels in `text/muted`, and the SORTED header in `text/primary`
 * with an `accent/indigo` arrow.
 *
 * ===========================================================================
 * ⚠ RULE 5 — "MODULES IS NOT BUILT, AND IT CANNOT BE" STOPPED BEING TRUE, AND THIS IS
 * THE RECORD OF WHY (IQ-9 / D19; migration 48, owner ruling 5)
 * ===========================================================================
 * This header used to open: *"MODULES IS NOT BUILT, AND IT CANNOT BE. Figma renders tags
 * there — 'Invoice', 'Excel Integration', 'Third Party Integration' — plus a `Pill/Count`
 * overflow. `inquiries` has no such column: the table is `doc_no, legacy_ref, country_id,
 * party_id, subject, details, tally_serial, tally_edition, tally_release, stage,
 * stage_entered_at, assigned_to, lost_reason, created_by/at, updated_at` and nothing else,
 * and hard rule 1 forbids adding one … Nothing in Phase 2 supplies the module list — it is
 * CRF work-module data, which is Phase 3+."* And below it: *"AND THE THREE NEW COLUMNS DO
 * NOT MAKE A MODULES COLUMN POSSIBLE … Nothing about them is a module."*
 *
 * Both were TRUE when written, and the second is still true of the three `tally_*`
 * columns. What changed is not a column on `inquiries` — the table's own column list is
 * exactly as quoted, and hard rule 1 still forbids adding to it — but a JOIN TABLE over
 * the ONE shared platform vocabulary: migration 48 adds `inquiry_platforms`
 * (`crf_platforms`' exact shape) and reshapes `platforms.applies_to` into
 * `SET('crf','project','inquiry')` under owner ruling 5, *"the third word is `inquiry`.
 * One list, three applicability words: crf, project, inquiry. A row may carry any
 * combination."* The values Figma draws — Invoice, Excel Integration, Third Party
 * Integration — are three of the four migrated Tally values, and migration 48 widened
 * exactly those four to admit `inquiry` (owner FORK 4), so the design's own tags are the
 * live vocabulary's own words. `InquiryDto.platforms` (always present, `[]` when none) is
 * what the column reads: NAMES from the server, in the vocabulary's order, `detail` null
 * on every list row by the API's own split.
 *
 * SO MODULES IS DRAWN, at its measured 230px, between STAGE and RECEIVED, as the design's
 * tags plus a `Pill/Count` overflow: the first two names as chips, then `+N` for the
 * rest, and an em dash for a row that carries nothing (the honest "none", not an empty
 * cell). NOTHING IN THIS FILE HOLDS A LABEL — the words come with the row, and a value the
 * owner has retired still reads correctly on the rows that carry it.
 *
 * ⚠ IT IS NOT SORTABLE, AND `sorting.ts` SAYS WHY IN ITS OWN VOICE: a set has no single
 * order. So it is a plain `<th>` beside the sortable ones — the same shape as the actions
 * column, with a visible label — and `SORT_COLUMNS` is unchanged. The `<colgroup>` note
 * below counts it.
 *
 * ⚠ QUOTED IS NOT DRAWN BECAUSE ITS COLUMN WAS REMOVED, WHICH IS THE OPPOSITE CASE. It
 * rendered `expected_value` through `formatMoney`, masked-aware, right-aligned — the idiom
 * `CrfsListPage` and `RenewalsPage` were both copied FROM. The owner removed the field
 * (migration 44, ruling Q6), so the cell has no value to render, and its 140px goes to
 * CUSTOMER (MODULES' 230 went there too until D19 took it back). There is nothing to hold
 * the header open for: an empty money column on every row is the shape this file has
 * always refused.
 *
 * ⚠ AND THIS TABLE NO LONGER RENDERS MONEY AT ALL. It was the ONLY masked-money surface in
 * the inquiry module, which is why its `text-fg-secondary` marker was one of the anchors
 * in `renewals/masked-marker.contrast.test.tsx`; that entry is retired there, in this same
 * commit, with the reason written beside it rather than deleted.
 *
 * ===========================================================================
 * A REAL <table>, NOT A GRID OF DIVS
 * ===========================================================================
 * The Figma frame is absolutely positioned cells, which converts most directly to a CSS
 * grid. It is built as a `<table>` instead: sortable columns need `aria-sort`, a
 * row/column relationship is what makes a screen reader able to read "Owner: Parshad
 * Joshi" instead of an orphaned name, and `<colgroup>` reproduces the measured widths
 * exactly. The visual result is the same; the semantics are not.
 */

import { useEffect, useRef } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ChevronDown, ChevronLeft, ChevronRight, Minus, MoreHorizontal } from 'lucide-react';
import type { InquiryDto, InquiryPlatformDto } from '../../lib/inquiries-api';
import { BOARD_STAGES, daysInStage, stageTabLabel } from '../../domain/stages';
import type { InquiryStage } from '../../domain/stages';
import type { CountryCode } from '../../domain/countries';
import { relativeTime } from '../../domain/events';
import { Chip, CountryBadge, DaysPill, StagePill } from '../../components/ui';
import { UserAvatar } from '../avatars/UserAvatar';
import { cn } from '../../lib/cn';
import { MenuItem } from './Toolbar';
import { RAIL_GEOMETRY, stageRailClass } from './stage-rail';
import { PER_PAGE_OPTIONS } from './query-state';
import type { InquiryQueryState } from './query-state';
import { formatDateLong } from './format';
import { SORT_COLUMNS, sortScope } from './sorting';
import type { SortColumn } from './sorting';

/** Header label per sortable column, verbatim from `Table/HeaderCell` instances. */
const COLUMN_LABEL: Readonly<Record<SortColumn, string>> = {
  customer: 'CUSTOMER',
  stage: 'STAGE',
  received: 'RECEIVED',
  days: 'DAYS IN STAGE',
  owner: 'OWNER',
};

/**
 * `Table/CheckboxCell` (6:165) — a 48px cell around a 16px box with a 1.5px
 * `border/strong` stroke, `radius` 4, `bg/surface` fill.
 *
 * Not the shared `components/ui/Checkbox`: that primitive always renders a VISIBLE label
 * (the design's `Input/Checkbox` has one), where a table cell must have an invisible one,
 * and the header box additionally needs `indeterminate` — which no DOM attribute can
 * express, only a property set through a ref. Same node, same geometry, different
 * labelling requirement.
 */
function CellCheckbox({
  checked,
  indeterminate = false,
  label,
  onChange,
}: {
  checked: boolean;
  indeterminate?: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}): React.JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current !== null) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);

  return (
    <span className="relative inline-flex size-4 align-middle">
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        aria-label={label}
        onChange={(event) => onChange(event.target.checked)}
        onClick={(event) => event.stopPropagation()}
        className="peer absolute inset-0 size-4 cursor-pointer appearance-none rounded-[4px] border-[1.5px] border-line-strong bg-surface checked:border-indigo checked:bg-indigo indeterminate:border-indigo indeterminate:bg-indigo"
      />
      {/*
       * The tick, and the dash for the header's indeterminate state.
       *
       * Figma's `Table/CheckboxCell` has only an "unchecked" variant, so the CHECKED look is not
       * in the design. It is taken from the one place this app already made that decision —
       * `components/ui/Checkbox` draws a lucide `Check` at strokeWidth 3 in `text/inverse` over the
       * indigo fill — rather than being decided a second time here. Without it a selected row's box
       * is a plain filled square, which reads as a rendering glitch rather than as a tick.
       */}
      <Check
        aria-hidden="true"
        strokeWidth={3}
        className="pointer-events-none absolute inset-0 m-auto size-3 text-fg-inverse opacity-0 peer-checked:opacity-100 peer-indeterminate:opacity-0"
      />
      <Minus
        aria-hidden="true"
        strokeWidth={3}
        className="pointer-events-none absolute inset-0 m-auto size-3 text-fg-inverse opacity-0 peer-indeterminate:opacity-100"
      />
    </span>
  );
}

export interface InquiryTableProps {
  rows: readonly InquiryDto[];
  /**
   * PHASE 3a · the country badge. A PROP, not `useCountryCode()`, because this
   * table is mounted directly in three test files under a bare `MemoryRouter`
   * with no `:cc` segment — the hook throws there, by design. `InquiryBoard`
   * already takes the same prop for the same reason, so the two views of the
   * same list now agree by construction.
   */
  countryCode: CountryCode;
  total: number;
  state: InquiryQueryState;
  selected: ReadonlySet<string>;
  onSelect: (id: string, selected: boolean) => void;
  onSelectPage: (selected: boolean) => void;
  onSort: (column: SortColumn) => void;
  onPage: (page: number) => void;
  onPerPage: (perPage: number) => void;
  /**
   * WT5 — `onPeek` IS GONE with the slide-over it opened. Row activation now
   * NAVIGATES to the record (`recordHref`): the record page is the peek at
   * full width, per the owner's kill ruling.
   */
  recordHref: (id: string) => string;
  onMoveStage: (row: InquiryDto, stage: InquiryStage) => void;
  onAddFollowup: (row: InquiryDto) => void;
  canStage: boolean;
  canEdit: boolean;
  /** Fresh clock for the days-in-stage chips; injectable so the tests are not flaky. */
  now?: Date;
}

export function InquiryTable({
  rows,
  countryCode,
  total,
  state,
  selected,
  onSelect,
  onSelectPage,
  onSort,
  onPage,
  onPerPage,
  recordHref,
  onMoveStage,
  onAddFollowup,
  canStage,
  canEdit,
  now = new Date(),
}: InquiryTableProps): React.JSX.Element {
  const navigate = useNavigate();
  const pageSelected = rows.length > 0 && rows.every((row) => selected.has(row.id));
  const someSelected = rows.some((row) => selected.has(row.id));
  const scope = sortScope(state.sort, state.dir, rows.length, total);

  const firstOnPage = total === 0 ? 0 : (state.page - 1) * state.perPage + 1;
  const lastOnPage = (state.page - 1) * state.perPage + rows.length;
  const hasPrev = state.page > 1;
  const hasNext = lastOnPage < total;

  return (
    <div className="flex flex-col gap-2">
      {scope === 'page' ? (
        <p
          role="status"
          className="self-start rounded-sm bg-warn-bg px-2.5 py-1 text-label text-warn-fg"
        >
          Sorted by {COLUMN_LABEL[state.sort].toLowerCase()} within this page —{' '}
          {rows.length} of {total} rows are loaded. Increase rows per page to sort them all.
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-md border border-line bg-surface">
        <table className="w-full min-w-[1100px] border-collapse text-left">
          <caption className="sr-only">
            Inquiries{state.stage === null ? '' : ` in ${stageTabLabel(state.stage)}`}, page{' '}
            {state.page}
          </caption>
          {/*
            ⚠ EIGHT `<col>`s — ONE MORE THAN THE POST-MIGRATION-44 SEVEN. The 140px QUOTED
            column dropped with `expected_value` (migration 44); the 230px MODULES column
            RETURNED with `inquiry_platforms` (migration 48, D19). `<colgroup>` is
            POSITIONAL, so a stale entry here would not fail anything; it would silently
            push STAGE's 190 onto MODULES and shift every measured width one cell to the
            right. The eight are checkbox · CUSTOMER (flexible) · STAGE · MODULES ·
            RECEIVED · DAYS IN STAGE · OWNER · actions. MODULES is the one body column
            that is NOT in `SORT_COLUMNS` (see the header), so the header row below places
            it by hand between the sortable STAGE and RECEIVED rather than mapping it.
          */}
          <colgroup>
            <col className="w-12" />
            <col />
            <col className="w-[190px]" />
            <col className="w-[230px]" />
            <col className="w-[170px]" />
            <col className="w-[140px]" />
            <col className="w-[190px]" />
            <col className="w-12" />
          </colgroup>

          <thead>
            <tr className="h-10 border-b border-line bg-subtle">
              <th scope="col" className="px-0 text-center align-middle">
                <CellCheckbox
                  checked={pageSelected}
                  indeterminate={someSelected}
                  label={`Select all ${String(rows.length)} inquiries on this page`}
                  onChange={onSelectPage}
                />
              </th>
              {SORT_COLUMNS.flatMap((column) => {
                const header = (
                  <SortableHeader
                    key={column}
                    column={column}
                    active={state.sort === column}
                    dir={state.dir}
                    onSort={onSort}
                    /*
                     * ⚠ NO NUMERIC HEADER SURVIVES. `numeric` right-aligned exactly one
                     * column — QUOTED — and it went with `expected_value`. Written as a
                     * literal `false` rather than dropped so the prop stays live for the
                     * next numeric column instead of being re-derived from Figma.
                     */
                    numeric={false}
                  />
                );
                /*
                 * MODULES sits after STAGE (design order: CUSTOMER · STAGE · MODULES ·
                 * RECEIVED …). Placed here, by hand, so the design's order survives without
                 * MODULES entering `SORT_COLUMNS` — it is not sortable (file header).
                 */
                return column === 'stage' ? [header, <ModulesHeader key="modules" />] : [header];
              })}
              <th scope="col">
                <span className="sr-only">Row actions</span>
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.map((row) => {
              const days = daysInStage(row.stage_entered_at, now);
              const isSelected = selected.has(row.id);
              return (
                /*
                 * PHASE 2.5a · LEVER 7 — THE WORST PAIR IN THE APP, AND IT WAS
                 * INVISIBLE BECAUSE IT ONLY EXISTED IN DARK MODE.
                 *
                 * `accent/indigo-soft` #eef0fe is one of the 49 MODE-INVARIANT
                 * Figma variables (tokens.css header, point 1) — it does not change
                 * in the Dark mode because Figma's Dark mode re-maps only seven
                 * variables. `text/primary` IS one of those seven, and flips to
                 * #f5f5f5. So a selected row in dark mode rendered near-white text
                 * on a near-white tint:
                 *
                 *   text/primary on accent/indigo-soft   15.03 light  ·  1.03 DARK
                 *
                 * 1.03:1 is worse than the 1.89:1 status pill this phase's gate is
                 * named after, and it shipped for the same reason: nobody selected a
                 * row with the dark theme on. Every foreground in the row was
                 * affected — the customer name, the money, the date, the owner.
                 *
                 * THE FIX CHANGES NO TOKEN. `bg-indigo/20` is the same
                 * `accent/indigo`, composited at 20% over whatever is behind it
                 * rather than substituted for it — the derived-shade technique
                 * `Button` (`hover:bg-indigo/90`) and `toast.tsx` (`bg-fg-inverse/25`)
                 * already use. MEASURED OFF THE PAINTED PIXEL, not modelled — Tailwind
                 * spells the modifier `color-mix(in oklab, …)` and the browser resolves
                 * it in OKLab, so the shipped value is #2b2f4b rather than the #2b2e4b
                 * sRGB arithmetic predicts:
                 *
                 *   text/primary    1.03 → 11.95     text/secondary  → 4.63
                 *   text/muted             →  4.95    tint vs an unselected row 1.26
                 *
                 * All three foregrounds clear AA and the row is MORE distinguishable
                 * from its neighbours than the light-mode original is (1.26 vs 1.13).
                 * `text/secondary` clears it by 0.13 and that is the thinnest margin
                 * this pass leaves anywhere; `lever7-contrast.test.ts` pins it.
                 *
                 * IT IS `dark:` ONLY, DELIBERATELY. Light mode measures 15.03:1 and
                 * is the design's own value; there is nothing to fix there, and
                 * repointing `--crf-accent-indigo-soft` would have been inventing a
                 * dark half for a Figma variable, which `tokens.css` forbids in its
                 * opening paragraph.
                 */
                <tr
                  key={row.id}
                  /* WT5 — the row OPENS THE RECORD (a `<tr>` cannot be an
                   * anchor, so the click navigates; the identity cell inside
                   * is the real link carrying the same href for middle-click
                   * and copy-address). */
                  onClick={() => navigate(recordHref(row.id))}
                  className={cn(
                    'crf-register-row group/row cursor-pointer border-b border-line last:border-b-0',
                    isSelected ? 'bg-indigo-soft dark:bg-indigo/20' : 'hover:bg-subtle',
                  )}
                >
                  {/*
                   * L6 + L3. The row's hover feedback is the SPINE, not a generic
                   * highlight: pointing at a row previews, in its own stage colour, the
                   * rail the record header it is about to open draws (WT5: the peek
                   * panel died; the record page inherited its rail and this preview).
                   *
                   * OPACITY ONLY, and that is the whole reason it is drawn this way. The
                   * hard rule allows `transform` and `opacity`; a `<tr>` inside a
                   * `border-collapse` table is the one element where a translate is a bad
                   * idea (it shifts the cells and misaligns the collapsed rules), and the
                   * existing `hover:bg-subtle` is a background swap, which is neither
                   * permitted property. So the tint stays INSTANT — untransitioned, exactly
                   * as it shipped — and the only thing that animates is this stripe fading
                   * in.
                   *
                   * `motion-safe:` is `@media (prefers-reduced-motion: no-preference)`
                   * verbatim, so under `reduce` the rail still appears, just without the
                   * fade. The duration comes from `--default-transition-duration`
                   * (= `--crf-motion-quick`, 120ms) — no number is typed here.
                   *
                   * `group-focus-within` matters as much as `group-hover`: a keyboard
                   * operator tabbing through the checkboxes gets the same cue a mouse does.
                   */}
                  <td className="relative text-center align-middle">
                    <span
                      aria-hidden="true"
                      className={cn(
                        'pointer-events-none absolute inset-y-0 left-0 opacity-0',
                        'group-hover/row:opacity-100 group-focus-within/row:opacity-100',
                        'motion-safe:transition-opacity',
                        RAIL_GEOMETRY,
                        stageRailClass(row.stage),
                      )}
                    />
                    <CellCheckbox
                      checked={isSelected}
                      label={`Select ${row.doc_no}`}
                      onChange={(next) => onSelect(row.id, next)}
                    />
                  </td>

                  {/* `Table/IdentityCell` (6:118/6:119): two lines, 2px apart.
                    * WT5 — a real `<Link>` now, because its destination is a
                    * ROUTE (the record), not a URL-param overlay. */}
                  <td className="px-3 align-middle">
                    <Link
                      to={recordHref(row.id)}
                      onClick={(event) => event.stopPropagation()}
                      className="flex max-w-full flex-col items-start gap-0.5 text-left"
                    >
                      <span className="truncate text-body font-medium text-fg">
                        {row.party_name ?? 'Unknown customer'}
                      </span>
                      {/*
                       * Figma's second line is a CONTACT PERSON ("Dinesh Gupta").
                       * `parties` has no contact-person column (spec §3), so the slot
                       * carries the two identifiers that DO exist and that an operator
                       * searches by: the document number and the subject.
                       *
                       * PHASE 3a — AND THE COUNTRY, AT THE HEAD OF THAT LINE. This is
                       * the row's metadata line, which is where a country belongs; the
                       * first line stays the customer's name alone. `max-w-full` +
                       * `min-w-0` on the text half keeps the existing truncation
                       * working now that the line is a flex row rather than one span.
                       */}
                      <span className="flex max-w-full items-center gap-1.5">
                        <CountryBadge code={countryCode} />
                        <span className="min-w-0 truncate text-label text-fg-muted">
                          {row.doc_no}
                          {row.subject === null || row.subject === '' ? '' : ` · ${row.subject}`}
                        </span>
                      </span>
                    </Link>
                  </td>

                  {/*
                   * L3 SPINE AUDIT — DELIBERATELY UNCHANGED.
                   * `StagePill` already reads its colour from `stageToneClasses`, i.e. from
                   * the same `stage/*` tokens the new rail reads, via the same one map in
                   * `domain/stages.ts`. There is nothing to reconcile: the pill and the rail
                   * cannot disagree because neither names a colour. Restyling it to "match
                   * the spine" would be restyling it to match itself.
                   */}
                  <td className="pl-3 align-middle">
                    <StagePill stage={row.stage} />
                  </td>

                  {/*
                   * MODULES (12:1504 …): the design's tags + `Pill/Count` overflow, from the
                   * row's own `platforms` — names from the server, vocabulary order. See the
                   * file header for the Rule-5 record of why this cell exists.
                   */}
                  <td className="px-3 align-middle">
                    <ModulesCell platforms={row.platforms} />
                  </td>

                  {/*
                    ⚠ THE QUOTED CELL STOOD HERE AND IS GONE WITH ITS COLUMN (migration 44).
                    It rendered `formatMoney(row.expected_value, row.currency)` and carried
                    the masked marker at `text-fg-secondary` — MEASURED 4.64 light / 5.85
                    dark, replacing a `text-fg-muted` that measured 2.63:1 on `bg/surface`
                    against a 4.5 floor. THAT FIX IS NOT LOST WITH THE CELL: this was the
                    site `CrfsListPage` and `RenewalsPage` copied the idiom FROM, both still
                    ship it, and both are still measured by
                    `renewals/masked-marker.contrast.test.tsx`. Only this row's entry there
                    retires, and it says so in place.
                  */}

                  {/* `Table/DateCell` (6:123/6:124): the date, then how long ago. */}
                  <td className="px-3 align-middle">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-body13 text-fg">{formatDateLong(row.created_at)}</span>
                      <span className="text-label text-fg-muted">
                        {row.created_at === null ? '' : relativeTime(row.created_at, now)}
                      </span>
                    </div>
                  </td>

                  <td className="pl-3 align-middle">
                    <DaysPill days={days} long />
                  </td>

                  {/*
                   * `Avatar/Pill` (12:1526): 24px avatar + `Body/13` name on `bg/subtle`.
                   *
                   * L4. `userId` is the identity the hue is derived from, not the name:
                   * `assigned_to` is the row's own foreign key, so two colleagues who
                   * share initials ("Sam Patel" / "Sara Price") cannot share a colour, and
                   * the same person is the same colour on every screen that has the id.
                   * `decorative` because the name is rendered right beside the disc —
                   * announcing it twice is noise, and that is what this call site did
                   * before (no `name`, hence no label) so nothing changes for a reader.
                   */}
                  <td className="pl-3 align-middle">
                    {row.assigned_to_name === null ? (
                      <span className="inline-flex items-center rounded-pill bg-subtle px-2.5 py-1 text-label text-fg-muted ring-1 ring-line">
                        Unassigned
                      </span>
                    ) : (
                      <span className="inline-flex max-w-full items-center gap-2 rounded-pill bg-subtle py-0.5 pl-0.5 pr-3">
                        {/* 2.5b: same disc, now carrying the photo when one exists. */}
                        <UserAvatar
                          name={row.assigned_to_name}
                          userId={row.assigned_to}
                          size={24}
                          decorative
                        />
                        <span className="truncate text-body13 text-fg">
                          {row.assigned_to_name}
                        </span>
                      </span>
                    )}
                  </td>

                  <td className="text-center align-middle">
                    <RowMenu
                      row={row}
                      recordHref={recordHref(row.id)}
                      canStage={canStage}
                      canEdit={canEdit}
                      onMoveStage={onMoveStage}
                      onAddFollowup={onAddFollowup}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* `Table/Pagination` (12:3252). */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface px-4 py-2">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger className="flex items-center gap-1 text-body13 text-fg-secondary hover:text-fg">
            {state.perPage} per page
            <ChevronDown aria-hidden="true" className="size-3.5" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="start"
              sideOffset={4}
              className="z-50 w-[160px] rounded-md border border-line bg-surface p-1.5 shadow-[var(--crf-shadow-overlay)]"
            >
              {PER_PAGE_OPTIONS.map((option) => (
                <MenuItem
                  key={option}
                  selected={state.perPage === option}
                  onSelect={() => onPerPage(option)}
                >
                  {option} per page
                </MenuItem>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        <div className="flex items-center gap-2">
          <p className="tnum text-body13 text-fg-secondary">
            {firstOnPage}–{lastOnPage} of {total}
          </p>
          <PageButton
            label="Previous page"
            disabled={!hasPrev}
            onClick={() => onPage(state.page - 1)}
          >
            <ChevronLeft aria-hidden="true" className="size-4" />
          </PageButton>
          <PageButton
            label="Next page"
            disabled={!hasNext}
            onClick={() => onPage(state.page + 1)}
          >
            <ChevronRight aria-hidden="true" className="size-4" />
          </PageButton>
        </div>
      </div>
    </div>
  );
}

/**
 * The MODULES header — a plain `<th>` with the design's label and NO sort affordance:
 * no button, no `aria-sort`, so assistive tech does not offer a sort the column will not
 * perform. `sorting.ts` records the choice.
 */
function ModulesHeader(): React.JSX.Element {
  return (
    <th scope="col" className="px-3">
      <span className="inline-flex items-center text-th uppercase text-fg-muted">MODULES</span>
    </th>
  );
}

/** At most this many names are drawn as chips before the rest collapse into `+N` — the
 * design draws two tags then the count pill; `crfs/domain.ts` `platformsSummary` makes the
 * same two-then-count choice for the CRF list. Not exported: a component file exporting a
 * constant trips `react-refresh/only-export-components`, and the test reads the RENDER. */
const MODULES_CHIPS_SHOWN = 2;

/**
 * The MODULES cell: the design's tags plus its `Pill/Count` overflow, from the row's own
 * `platforms` (`InquiryPlatformDto[]`, vocabulary order, names from the server).
 *
 * `—` for a row that carries nothing — an EMPTY cell would read as a rendering gap on a
 * table whose every other cell always shows something, and "no requirements recorded" is
 * a fact about the row worth one glyph. `title` on the overflow pill lists the collapsed
 * names, so the +N is inspectable without opening the record.
 */
export function ModulesCell({
  platforms,
}: {
  platforms: readonly InquiryPlatformDto[];
}): React.JSX.Element {
  if (platforms.length === 0) {
    return <span className="text-label text-fg-muted">—</span>;
  }
  const shown = platforms.slice(0, MODULES_CHIPS_SHOWN);
  const rest = platforms.slice(MODULES_CHIPS_SHOWN);
  return (
    <span className="flex max-w-full flex-wrap items-center gap-1">
      {shown.map((platform) => (
        <Chip key={platform.platform_id} className="max-w-full">
          <span className="truncate">{platform.name}</span>
        </Chip>
      ))}
      {rest.length === 0 ? null : (
        <span
          title={rest.map((platform) => platform.name).join(' · ')}
          className="inline-flex h-5 items-center rounded-pill bg-subtle px-2 text-label text-fg-secondary ring-1 ring-line"
        >
          +{rest.length}
        </span>
      )}
    </span>
  );
}

function SortableHeader({
  column,
  active,
  dir,
  onSort,
  numeric,
}: {
  column: SortColumn;
  active: boolean;
  dir: 'asc' | 'desc';
  onSort: (column: SortColumn) => void;
  numeric: boolean;
}): React.JSX.Element {
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn('px-3', numeric && 'text-right')}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={cn(
          'inline-flex items-center gap-1 text-th uppercase',
          active ? 'text-fg' : 'text-fg-muted hover:text-fg-secondary',
        )}
      >
        {COLUMN_LABEL[column]}
        {/* The design's own indicator: an `accent/indigo` arrow beside the active label. */}
        <span aria-hidden="true" className={cn('text-label', active ? 'text-indigo' : 'invisible')}>
          {dir === 'asc' ? '↑' : '↓'}
        </span>
      </button>
    </th>
  );
}

function PageButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-7 items-center justify-center rounded-sm border border-line bg-surface text-fg-secondary hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}

/** `Table/ActionsCell` (6:171) — the "⋯" the design draws, with real items behind it. */
function RowMenu({
  row,
  recordHref,
  canStage,
  canEdit,
  onMoveStage,
  onAddFollowup,
}: {
  row: InquiryDto;
  recordHref: string;
  canStage: boolean;
  canEdit: boolean;
  onMoveStage: (row: InquiryDto, stage: InquiryStage) => void;
  onAddFollowup: (row: InquiryDto) => void;
}): React.JSX.Element {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={`Actions for ${row.doc_no}`}
        onClick={(event) => event.stopPropagation()}
        className="flex size-8 items-center justify-center rounded-sm text-fg-secondary hover:bg-subtle hover:text-fg"
      >
        <MoreHorizontal aria-hidden="true" className="size-4" />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          onClick={(event) => event.stopPropagation()}
          className="z-50 w-[240px] rounded-md border border-line bg-surface p-1.5 shadow-[var(--crf-shadow-overlay)]"
        >
          <DropdownMenu.Item asChild>
            <Link
              to={recordHref}
              className="flex cursor-pointer items-center rounded-sm px-2 py-1.5 text-body13 text-fg outline-none data-[highlighted]:bg-subtle"
            >
              Open full record
            </Link>
          </DropdownMenu.Item>

          {canEdit ? (
            <MenuItem onSelect={() => onAddFollowup(row)}>Add follow-up</MenuItem>
          ) : null}

          {canStage ? (
            <>
              <DropdownMenu.Separator className="my-1 h-px bg-line" />
              <DropdownMenu.Label className="px-2 py-1 text-th uppercase text-fg-muted">
                Move to stage
              </DropdownMenu.Label>
              {BOARD_STAGES.filter((stage) => stage !== row.stage).map((stage) => (
                <MenuItem key={stage} onSelect={() => onMoveStage(row, stage)}>
                  {stageTabLabel(stage)}
                </MenuItem>
              ))}
            </>
          ) : null}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
